import { Breakpoint, LessonInfobox, UseState } from '@elearning-platform/ui';
import React, { useEffect, useMemo, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { useMediaQuery } from 'react-responsive';

import {
  AdditionalProfitsCalculator,
  AnimationsWithStatementChoice,
  BenefitCalculator,
  CenteredContentSlide,
  InsuranceCalculator,
  InsuranceTable,
  InsuranceValueCalculator,
  InteractionSlide,
  SelectVideo,
  TableWithText,
} from '../../componentsV2';
import { ScrollElearningContentSlideType } from '../../enums';
import { PageData } from '../../interfaces';
import { getJsonData } from '../../services/api';
import { ScrollElearningContent } from '../../types';
import { AnimWithText } from '../animWithText/animWithText.component';
import { ChooseAnswer } from '../chooseAnswer/chooseAnswer.component';
import { ImageWithText } from '../imageWithText/imageWithText.component';
import { TextBox } from '../textBox/textBox.component';
import { VideoSlide } from '../videoSlide/videoSlide.component';

import { PageDataContext } from './page.context';
import { Container, LessonInfoboxWrapper } from './page.styled';
import { PageProps } from './page.types';

export const Page: React.FC<PageProps> = (props: PageProps): JSX.Element | null => {
  const {
    data,
    baseUrl,
    callComplete,
    currentSlideCounter,
    slidesOverallCounter,
    onFloatClick,
    onVisibleChange,
    onScrollButtonClick,
    setGradientBg,
    setWithoutBackgroundImage,
  }: PageProps = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dataSrc: string | undefined = data.children[0].children[0].params.find((param: any): boolean => param.name === 'config')?.value;
  const isSmallDesktop: boolean = useMediaQuery({
    query: Breakpoint.SmallDesktop,
  });
  const [jsonData, setJsonData]: UseState<ScrollElearningContent | null> = useState<ScrollElearningContent | null>(null);
  const pageData: PageData | null = useMemo(
    (): PageData | null => {
      if (!jsonData) {
        return null;
      }
      // TODO: remove after refactor old interactions
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const untypedJsonData: any = jsonData;

      return {
        title: data.children[0].params.title,
        description: untypedJsonData.htmlText,
        helperText: untypedJsonData.helperText,
        assetSrc: untypedJsonData.src ? baseUrl + untypedJsonData.src : undefined,
        placeholderSrc: untypedJsonData.placeholderSrc ? baseUrl + untypedJsonData.placeholderSrc : undefined,
        fullSizeAnim: untypedJsonData.fullSizeAnim,
        // @deprecated - don't use
        additionalTitle: untypedJsonData.additionalTitle,
        additionalBgAnimSrc: untypedJsonData.additionalBgAnimSrc ? baseUrl + untypedJsonData.additionalBgAnimSrc : undefined,
        question: untypedJsonData.question,
        answers: untypedJsonData.answers,
        multipleChoice: untypedJsonData.multipleChoice,
        currentSlideCounter,
        slidesOverallCounter,
        elearningContent: jsonData,
        baseUrl,
      };
    },
    [jsonData, baseUrl, currentSlideCounter, slidesOverallCounter, data.children]
  );

  useEffect((): void => getJsonData(baseUrl + dataSrc, setJsonData), [baseUrl, dataSrc]);

  useEffect((): void => {
    if (
      pageData?.elearningContent
      && 'withGradientBgStyle' in pageData.elearningContent
      && pageData.elearningContent.withGradientBgStyle
    ) {
      setGradientBg(true);
    }
  }, [pageData?.elearningContent, setGradientBg]);

  useEffect((): void => {
    if (
      pageData?.elearningContent
      && 'withoutBgImage' in pageData.elearningContent
      && pageData.elearningContent.withoutBgImage
    ) {
      setWithoutBackgroundImage(true);
    }
  }, [pageData?.elearningContent, setWithoutBackgroundImage]);

  const handleInViewChange: (inView: boolean) => void = (inView: boolean): void => {
    if (inView) {
      onVisibleChange();
    }
  };

  const handleComplete: () => void = (): void => {
    const interactionId: number = data.children[0].children[0].id;
    callComplete(interactionId, data.id, currentSlideCounter === slidesOverallCounter);
  };

  if (!pageData?.elearningContent) {
    return null;
  }

  return (
    <PageDataContext.Provider value={pageData}>
      <InView onChange={handleInViewChange}>
        {/* TODO: fixme */}
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {({ ref }: any): JSX.Element => (
          <Container ref={ref}>
            {pageData?.elearningContent.type === ScrollElearningContentSlideType.Video && (
              <VideoSlide callComplete={handleComplete} completed={data.completed} />
            )}
            {pageData?.elearningContent.type === ScrollElearningContentSlideType.AnimWithText && (
              <AnimWithText
                callComplete={handleComplete}
                completed={data.completed}
                onFloatClick={onFloatClick}
                onScrollButtonClick={onScrollButtonClick}
                withGradientBgStyle={pageData?.elearningContent?.withGradientBgStyle}
              />
            )}
            {pageData?.elearningContent.type === ScrollElearningContentSlideType.ImageWithText && (
              <ImageWithText callComplete={handleComplete} completed={data.completed} />
            )}
            {pageData?.elearningContent.type === ScrollElearningContentSlideType.ChooseAnswer && (
              <ChooseAnswer callComplete={handleComplete} />
            )}
            {pageData?.elearningContent.type === ScrollElearningContentSlideType.TextBox && (
              <TextBox
                callComplete={handleComplete}
                completed={data.completed}
                fluidHeight={undefined}
                isInteraction
              />
            )}
            {pageData?.elearningContent.type === ScrollElearningContentSlideType.CenteredContentSlide && (
              <CenteredContentSlide
                baseUrl={baseUrl}
                callComplete={handleComplete}
                completed={!!data.completed}
                data={pageData?.elearningContent.centeredContentSlideDetails}
              />
            )}
            {pageData?.elearningContent.type === ScrollElearningContentSlideType.InsuranceCalculator && (
              <InsuranceCalculator
                baseUrl={baseUrl}
                callComplete={handleComplete}
                centeredContentSlideWrapperData={pageData?.elearningContent.centeredContentSlideWrapperData}
                completed={!!data.completed}
              />
            )}
            {pageData?.elearningContent.type === ScrollElearningContentSlideType.InsuranceTable && (
              <InsuranceTable callComplete={handleComplete} completed={!!data.completed} />
            )}
            {[
              ScrollElearningContentSlideType.ChoiceQuiz,
              ScrollElearningContentSlideType.ProgressiveQuiz,
              ScrollElearningContentSlideType.MatchToCategory,
              ScrollElearningContentSlideType.DragAndDrop,
              ScrollElearningContentSlideType.InteractionSlideCustomAction,
            ].includes(pageData?.elearningContent.type) && (
              <InteractionSlide baseUrl={baseUrl} callComplete={handleComplete} completed={!!data.completed} />
            )}
            {pageData?.elearningContent.type === ScrollElearningContentSlideType.AdditionalProfitsCalculator && (
              <AdditionalProfitsCalculator
                baseUrl={baseUrl}
                callComplete={handleComplete}
                centeredContentSlideWrapperData={pageData?.elearningContent.centeredContentSlideWrapperData}
                completed={!!data.completed}
              />
            )}
            {pageData?.elearningContent.type === ScrollElearningContentSlideType.InsuranceValueCalculator && (
              <InsuranceValueCalculator
                baseUrl={baseUrl}
                callComplete={handleComplete}
                centeredContentSlideWrapperData={pageData?.elearningContent.centeredContentSlideWrapperData}
                completed={!!data.completed}
              />
            )}
            {pageData?.elearningContent.type === ScrollElearningContentSlideType.AnimationsWithStatementChoice && (
              <AnimationsWithStatementChoice
                baseUrl={baseUrl}
                callComplete={handleComplete}
                completed={!!data.completed}
                content={pageData?.elearningContent.animationsWithStatementsData}
              />
            )}
            {pageData?.elearningContent.type === ScrollElearningContentSlideType.SelectVideo && (
              <SelectVideo baseUrl={baseUrl} callComplete={handleComplete} videosData={pageData.elearningContent.videos} />
            )}
            {pageData?.elearningContent.type === ScrollElearningContentSlideType.TableWthText && (
              <TableWithText
                callComplete={handleComplete}
                completed={!!data.completed}
                content={pageData?.elearningContent.tableWithTextData}
              />
            )}
            {pageData?.elearningContent.type === ScrollElearningContentSlideType.BenefitCalculator && (
              <BenefitCalculator
                baseUrl={baseUrl}
                callComplete={handleComplete}
                centeredContentSlideWrapperData={pageData?.elearningContent.centeredContentSlideWrapperData}
                completed={!!data.completed}
                data={pageData?.elearningContent.benefitCalculatorData}
              />
            )}
            {!!(
              pageData?.elearningContent
              && 'lessonInfoboxHeadline' in pageData.elearningContent
              && pageData.elearningContent.lessonInfoboxHeadline
              && 'lessonInfoboxSupportingText' in pageData.elearningContent
              && pageData?.elearningContent.lessonInfoboxSupportingText
            ) && (
              <LessonInfoboxWrapper>
                <LessonInfobox
                  headline={pageData?.elearningContent.lessonInfoboxHeadline}
                  image={isSmallDesktop
                    ? baseUrl + pageData?.elearningContent.lessonInfoboxImage
                    : baseUrl + pageData?.elearningContent.lessonInfoboxMobileImage
                  }
                  supportingText={pageData?.elearningContent.lessonInfoboxSupportingText}
                />
              </LessonInfoboxWrapper>
            )}
          </Container>
        )}
      </InView>
    </PageDataContext.Provider>
  );
};
